import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import GuestMessages from "./GuestMessages";
import "./MainInvitation.css";

const MainInvitation: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const { scrollYProgress } = useScroll();

  // Countdown state
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Love story timeline data
  const loveStory = [
    {
      icon: "💫",
      title: "Pertemuan Pertama",
      date: "Takdir mempertemukan",
      desc: "Allah mempertemukan kami dalam perjalanan hidup yang tak terduga.",
    },
    {
      icon: "💌",
      title: "Saling Mengenal",
      date: "Taaruf",
      desc: "Melalui proses taaruf, kami saling mengenal lebih dalam dengan niat yang baik.",
    },
    {
      icon: "💍",
      title: "Lamaran",
      date: "Khitbah",
      desc: "Dengan restu kedua keluarga, kami melangkah menuju jenjang pernikahan.",
    },
    {
      icon: "🕌",
      title: "Akad Nikah",
      date: "06 April 2026",
      desc: "InsyaAllah, kami akan mengikat janji suci di hadapan Allah SWT.",
    },
  ];

  // Romantic quotes that rotate
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const romanticQuotes = [
    { text: "Dua jiwa yang saling melengkapi, dua hati yang menjadi satu.", source: "" },
    { text: "Cinta sejati adalah ketika dua orang saling mendoakan kebaikan.", source: "" },
    { text: "Pernikahan adalah menyempurnakan separuh agama.", source: "HR. Anas bin Malik" },
  ];

  const galleryImages = [
    "/DSC_9923.jpeg",
    "/DSC_9925.jpeg",
    "/DSC_9936.jpeg",
    "/DSC_9951.jpeg",
    "/1731842193967.jpeg",
    "/1733978120392.jpeg",
  ];

  const coupleNames = {
    man: {
      fullName: "Imran Mursalin",
      nickname: "Imran",
      parents: "Putra ketiga dari Bpk. Jasapta & Ibu Rahmaina Pardede",
    },
    woman: {
      fullName: "Sofiatun Jannah",
      nickname: "Sofia",
      parents: "Putri ke empat dari Bpk. Rusli & Ibu Yuslidar",
    },
  };

  const akadDetails = {
    date: "Senin, 06 April 2026",
    time: "09:00 WIB - Selesai",
    address: "KUA Indrapuri, Desa Limo Blang, Kec. Indrapuri",
  };

  const resepsiDetails = {
    date: "Senin, 06 April 2026",
    time: "11:00 WIB - Selesai",
    address: "Desa Limo Blang, Kec. Indrapuri",
  };

  const toggleMusic = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current
          .play()
          .catch((e) => console.log("Audio play failed:", e));
      } else {
        audioRef.current.pause();
      }
    }
  };

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  }, [galleryImages.length]);

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + galleryImages.length) % galleryImages.length,
    );
  };

  useEffect(() => {
    const timer = setInterval(nextImage, 5000);
    return () => clearInterval(timer);
  }, [nextImage]);

  // Quote rotation
  useEffect(() => {
    const quoteTimer = setInterval(() => {
      setCurrentQuoteIndex((prev) => (prev + 1) % romanticQuotes.length);
    }, 6000);
    return () => clearInterval(quoteTimer);
  }, [romanticQuotes.length]);

  // Countdown timer
  useEffect(() => {
    const weddingDate = new Date("2026-04-06T09:00:00+07:00").getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const gap = weddingDate - now;

      if (gap > 0) {
        setCountdown({
          days: Math.floor(gap / (1000 * 60 * 60 * 24)),
          hours: Math.floor((gap % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((gap % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((gap % (1000 * 60)) / 1000),
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Set audio volume and event listeners
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      const audio = audioRef.current;
      audio.addEventListener("play", handlePlay);
      audio.addEventListener("pause", handlePause);

      return () => {
        audio.removeEventListener("play", handlePlay);
        audio.removeEventListener("pause", handlePause);
      };
    }
  }, []);

  // Autoplay on user interaction
  useEffect(() => {
    const handleUserInteraction = (e: Event) => {
      if (audioRef.current && !isPlaying) {
        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
            document.removeEventListener("click", handleUserInteraction);
            document.removeEventListener("touchstart", handleUserInteraction);
            document.removeEventListener("scroll", handleUserInteraction);
            document.removeEventListener("keydown", handleUserInteraction);
          })
          .catch(() => {});
      }
    };

    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);
    document.addEventListener("scroll", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);

    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
      document.removeEventListener("scroll", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
    };
  }, [isPlaying]);

  // Section animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.9, ease: "easeOut" as const },
    },
  };

  return (
    <div className="main-invitation">
      {/* Scroll Progress Bar */}
      <motion.div
        className="scroll-progress"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Global Floating Petals */}
      <div className="global-petals">
        {Array.from({ length: 10 }).map((_, i) => (
          <span
            key={`petal-${i}`}
            className={`global-petal petal-${i % 4}`}
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 15}s`,
              fontSize: `${12 + Math.random() * 12}px`,
              opacity: 0.15 + Math.random() * 0.15,
            }}
          >
            {["🌸", "🏵️", "💮", "🌺"][i % 4]}
          </span>
        ))}
      </div>
      {/* Hidden Audio Element */}
      <audio ref={audioRef} loop preload="auto">
        <source src="/wedding-music.mp3" type="audio/mpeg" />
      </audio>

      {/* Music Toggle Button */}
      <motion.button
        className="music-toggle"
        onClick={toggleMusic}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isPlaying ? "🔇" : "🔊"}
      </motion.button>

      {/* Music Prompt */}
      {!isPlaying && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="music-prompt"
        >
          <p>🎵 Tap anywhere to play music</p>
        </motion.div>
      )}

      {/* Hero Section with Main Photo */}
      <section className="hero-section">
        <div className="hero-image-container">
          <motion.img
            src="/main.jpg"
            alt="Couple"
            className="hero-image"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 3, ease: "easeOut" }}
          />
          <div className="hero-overlay">
            {/* Floating hearts in hero */}
            <div className="hero-hearts">
              {Array.from({ length: 8 }).map((_, i) => (
                <span
                  key={`hero-heart-${i}`}
                  className="hero-heart"
                  style={{
                    left: `${10 + Math.random() * 80}%`,
                    animationDelay: `${Math.random() * 8}s`,
                    animationDuration: `${6 + Math.random() * 6}s`,
                    fontSize: `${10 + Math.random() * 14}px`,
                  }}
                >
                  {["💕", "❤️", "💗", "🤍"][i % 4]}
                </span>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="hero-text"
            >
              <h1>
                {coupleNames.man.nickname} & {coupleNames.woman.nickname}
              </h1>
              <p>Save The Date</p>
              <div className="hero-date-badge">06 • 04 • 2026</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ornamental Divider */}
      <div className="ornamental-divider">
        <span className="orn-line" />
        <span className="orn-icon">💕</span>
        <span className="orn-line" />
      </div>

      {/* Countdown Timer */}
      <motion.section
        className="countdown-section"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <h2 className="countdown-title">Counting Down To Our Day</h2>
        <div className="countdown-grid">
          {[
            { value: countdown.days, label: "Hari" },
            { value: countdown.hours, label: "Jam" },
            { value: countdown.minutes, label: "Menit" },
            { value: countdown.seconds, label: "Detik" },
          ].map((item, i) => (
            <motion.div
              key={item.label}
              className="countdown-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
            >
              <div className="countdown-number">
                {String(item.value).padStart(2, "0")}
              </div>
              <div className="countdown-label">{item.label}</div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Animated Romantic Section (Quran verse) */}
      <section className="romantic-verse-section">
        <div className="verse-particles">
          {Array.from({ length: 20 }).map((_, i) => (
            <span
              key={`particle-${i}`}
              className={`r-particle p-${i % 5}`}
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 6}s`,
                animationDuration: `${6 + Math.random() * 8}s`,
              }}
            />
          ))}
        </div>
        <div className="verse-hearts">
          {Array.from({ length: 10 }).map((_, i) => (
            <span
              key={`verse-heart-${i}`}
              className="verse-floating-heart"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 8}s`,
                animationDuration: `${8 + Math.random() * 6}s`,
                fontSize: `${12 + Math.random() * 14}px`,
                opacity: 0.3 + Math.random() * 0.3,
              }}
            >
              {["💗", "💕", "🤍", "💞"][i % 4]}
            </span>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="verse-content"
        >
          <p className="verse-bismillah">
            بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم
          </p>
          <p className="verse-text">
            "Dan di antara tanda-tanda kekuasaan-Nya ialah Dia menciptakan
            untukmu pasangan hidup dari jenismu sendiri supaya kamu merasa
            tenteram kepadanya. Dan Dia menjadikan rasa cinta dan kasih sayang
            di antara hatimu."
          </p>
          <p className="verse-ref">— QS. Ar-Rum: 21 —</p>
        </motion.div>
      </section>

      {/* Ornamental Divider */}
      <div className="ornamental-divider light">
        <span className="orn-line" />
        <span className="orn-icon">✦</span>
        <span className="orn-line" />
      </div>

      {/* Couple Details Section */}
      <section className="couple-section">
        <div className="couple-floating-hearts">
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={`couple-heart-${i}`}
              className="couple-heart"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${8 + Math.random() * 8}s`,
                fontSize: `${10 + Math.random() * 12}px`,
              }}
            >
              {["💕", "💗", "🤍", "💞"][i % 4]}
            </span>
          ))}
        </div>
        <div className="couple-container">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="person-card"
          >
            <div className="person-image">
              <img src="/imran.jpeg" alt={coupleNames.man.fullName} />
            </div>
            <h3>{coupleNames.man.fullName}</h3>
            <p>{coupleNames.man.nickname}</p>
            <p className="parent-info">{coupleNames.man.parents}</p>
          </motion.div>

          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="heart-divider"
          >
            💕
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="person-card"
          >
            <div className="person-image">
              <img src="/sofia.jpeg" alt={coupleNames.woman.fullName} />
            </div>
            <h3>{coupleNames.woman.fullName}</h3>
            <p>{coupleNames.woman.nickname}</p>
            <p className="parent-info">{coupleNames.woman.parents}</p>
          </motion.div>
        </div>
      </section>

      {/* Ornamental Divider */}
      <div className="ornamental-divider">
        <span className="orn-line" />
        <span className="orn-icon">💍</span>
        <span className="orn-line" />
      </div>

      {/* Wedding Details Section */}
      <section className="wedding-details">
        <motion.h2
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="details-main-title"
        >
          Waktu & Tempat
        </motion.h2>
        <div className="details-two-col">
          {/* Akad Nikah */}
          <motion.div
            initial={{ opacity: 0, x: -60, rotateY: 15 }}
            whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="details-card"
          >
            <div className="details-card-icon">💍</div>
            <h3>Akad Nikah</h3>
            <div className="details-divider" />
            <p className="details-date">{akadDetails.date}</p>
            <p className="details-time">{akadDetails.time}</p>
            <div className="details-location">
              <span className="location-pin">📍</span>
              <p>{akadDetails.address}</p>
            </div>
          </motion.div>

          {/* Resepsi */}
          <motion.div
            initial={{ opacity: 0, x: 60, rotateY: -15 }}
            whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="details-card"
          >
            <div className="details-card-icon">🎉</div>
            <h3>Resepsi</h3>
            <div className="details-divider" />
            <p className="details-date">{resepsiDetails.date}</p>
            <p className="details-time">{resepsiDetails.time}</p>
            <div className="details-location">
              <span className="location-pin">📍</span>
              <p>{resepsiDetails.address}</p>
            </div>
            <a
              href="https://maps.app.goo.gl/pigHVFPMMbNFeEQVA?g_st=aw"
              target="_blank"
              rel="noopener noreferrer"
              className="maps-btn"
            >
              📍 Buka Google Maps
            </a>
          </motion.div>
        </div>
      </section>

      {/* Love Story Timeline */}
      <section className="love-story-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="love-story-title"
        >
          Our Love Story
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="love-story-subtitle"
        >
          Perjalanan cinta kami yang diberkahi
        </motion.p>
        <div className="timeline">
          {loveStory.map((item, i) => (
            <motion.div
              key={i}
              className={`timeline-item ${i % 2 === 0 ? "left" : "right"}`}
              initial={{ opacity: 0, x: i % 2 === 0 ? -60 : 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
            >
              <div className="timeline-dot">
                <span>{item.icon}</span>
              </div>
              <div className="timeline-card">
                <h4>{item.title}</h4>
                <span className="timeline-date">{item.date}</span>
                <p>{item.desc}</p>
              </div>
            </motion.div>
          ))}
          <div className="timeline-line" />
        </div>
      </section>

      {/* Ornamental Divider */}
      <div className="ornamental-divider">
        <span className="orn-line" />
        <span className="orn-icon">✦</span>
        <span className="orn-line" />
      </div>

      {/* Romantic Quote Section */}
      <section className="love-quote-section">
        <div className="quote-sparkles">
          {Array.from({ length: 15 }).map((_, i) => (
            <span
              key={`q-sparkle-${i}`}
              className="quote-sparkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          ))}
        </div>
        <motion.div
          className="quote-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <span className="quote-mark">"</span>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuoteIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
              className="quote-text-wrap"
            >
              <p className="quote-text">
                {romanticQuotes[currentQuoteIndex].text}
              </p>
              {romanticQuotes[currentQuoteIndex].source && (
                <p className="quote-source">
                  — {romanticQuotes[currentQuoteIndex].source}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
          <span className="quote-mark bottom">"</span>
          <div className="quote-dots">
            {romanticQuotes.map((_, i) => (
              <span
                key={i}
                className={`quote-dot ${i === currentQuoteIndex ? "active" : ""}`}
                onClick={() => setCurrentQuoteIndex(i)}
              />
            ))}
          </div>
        </motion.div>
      </section>

      {/* Ornamental Divider */}
      <div className="ornamental-divider light">
        <span className="orn-line" />
        <span className="orn-icon">📸</span>
        <span className="orn-line" />
      </div>

      {/* Gallery Section */}
      <section className="gallery-section">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Our Moments
        </motion.h2>
        <div className="gallery-container">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentImageIndex}
              src={galleryImages[currentImageIndex]}
              alt={`Gallery ${currentImageIndex + 1}`}
              className="gallery-image"
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.7, ease: "easeInOut" }}
            />
          </AnimatePresence>
          <button className="gallery-btn prev" onClick={prevImage}>
            ‹
          </button>
          <button className="gallery-btn next" onClick={nextImage}>
            ›
          </button>
          <div className="gallery-dots">
            {galleryImages.map((_, index) => (
              <span
                key={index}
                className={`dot ${index === currentImageIndex ? "active" : ""}`}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </div>
        </div>
      </section>



      {/* Ornamental Divider */}
      <div className="ornamental-divider light">
        <span className="orn-line" />
        <span className="orn-icon">🎁</span>
        <span className="orn-line" />
      </div>

      {/* Wedding Gift Section */}
      <section className="gift-section">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="gift-title"
        >
          Wedding Gift
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="gift-subtitle"
        >
          Doa restu Anda merupakan karunia yang sangat berarti bagi kami.
        </motion.p>
        <div className="gift-cards-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="gift-card"
          >
            <img
              src="/BSI-Bank-Syariah-Indonesia-Logo-PNG480p-Vector69Com-1.png"
              alt="BSI Logo"
              className="bank-logo"
            />
            <p className="bank-name">Bank Syariah Indonesia</p>
            <p className="bank-account">1134128874</p>
            <p className="bank-holder">a.n Imran Mursalin</p>
            <button
              className="copy-btn"
              onClick={() => {
                navigator.clipboard.writeText("1134128874");
                const btn = document.querySelector(
                  ".copy-btn",
                ) as HTMLButtonElement;
                if (btn) {
                  btn.textContent = "✓ Tersalin!";
                  setTimeout(() => {
                    btn.textContent = "Salin No. Rekening";
                  }, 2000);
                }
              }}
            >
              Salin No. Rekening
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="gift-card"
          >
            <img
              src="/BSI-Bank-Syariah-Indonesia-Logo-PNG480p-Vector69Com-1.png"
              alt="BSI Logo"
              className="bank-logo"
            />
            <p className="bank-name">Bank Syariah Indonesia</p>
            <p className="bank-account">7165079207</p>
            <p className="bank-holder">a.n Sofiatun Jannah</p>
            <button
              className="copy-btn-2"
              onClick={() => {
                navigator.clipboard.writeText("7165079207");
                const btn = document.querySelector(
                  ".copy-btn-2",
                ) as HTMLButtonElement;
                if (btn) {
                  btn.textContent = "✓ Tersalin!";
                  setTimeout(() => {
                    btn.textContent = "Salin No. Rekening";
                  }, 2000);
                }
              }}
            >
              Salin No. Rekening
            </button>
          </motion.div>
        </div>
      </section>

      {/* Guest Messages */}
      <GuestMessages />

      {/* Footer with Quote */}
      <footer className="invitation-footer">
        <div className="footer-hearts">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={`footer-heart-${i}`}
              className="footer-heart"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 8}s`,
                animationDuration: `${8 + Math.random() * 6}s`,
                fontSize: `${10 + Math.random() * 10}px`,
              }}
            >
              {["💕", "🤍", "💗"][i % 3]}
            </span>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="footer-content"
        >
          <p className="footer-greeting">
            Merupakan suatu kehormatan dan kebahagiaan bagi kami apabila
            Bapak/Ibu/Saudara/i berkenan hadir untuk memberikan doa restu kepada
            kedua mempelai.
          </p>
          <p className="footer-thanks">
            Atas kehadiran dan doa restunya, kami mengucapkan terima kasih.
          </p>
          <p className="footer-couple">Imran & Sofia</p>
          <p className="footer-wassalam">
            Wassalamu'alaikum Warahmatullahi Wabarakatuh
          </p>
        </motion.div>
      </footer>
    </div>
  );
};

export default MainInvitation;
